import { z } from 'zod';

/**
 * Runtime configuration, fetched from the API rather than compiled in.
 *
 * The same built bundle has to promote from dev through to production without
 * a rebuild -- that is what makes the artefact an auditor sees in the change
 * record the same bytes that are running. Baking the tenant and client IDs in
 * at build time would mean one bundle per environment and three chances for
 * the wrong one to ship.
 */

const appConfigSchema = z.object({
  env: z.enum(['dev', 'test', 'staging', 'prod']),
  build: z.object({ sha: z.string(), time: z.string() }),
  auth: z.object({
    tenantId: z.string(),
    spaClientId: z.string(),
    apiScope: z.string(),
    devBypass: z.boolean(),
  }),
  limits: z.object({
    interactiveRowCap: z.number(),
    interactiveTimeoutMs: z.number(),
    presignedUrlTtlSeconds: z.number(),
  }),
  /**
   * Whether this deployment offers progress streaming. The SPA polls more
   * frequently when it does not, rather than pretending a stream exists and
   * failing intermittently behind a proxy that buffers.
   */
  sse: z.object({ enabled: z.boolean(), heartbeatMs: z.number() }).default({
    enabled: true,
    heartbeatMs: 20_000,
  }),
});

export type AppConfig = z.infer<typeof appConfigSchema>;

let cached: AppConfig | null = null;

/**
 * Loaded once before React mounts, because MSAL cannot be constructed without
 * the tenant and client IDs. A failure here is fatal and shown as a full-page
 * error: there is nothing useful to render without it.
 */
export async function loadAppConfig(): Promise<AppConfig> {
  if (cached) return cached;

  const response = await fetch('/api/config', {
    headers: { accept: 'application/json' },
    // Must not be served from cache: a stale client ID after a tenant change
    // produces a sign-in loop that no amount of retrying fixes.
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Could not load application configuration (HTTP ${response.status})`);
  }

  cached = appConfigSchema.parse(await response.json());
  return cached;
}

export function appConfig(): AppConfig {
  if (!cached) throw new Error('Application configuration has not been loaded');
  return cached;
}
